import { useReducer, useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import MessageAlert from 'app/components/MessageAlert'
import effects from 'app/effects/effects'
import * as R from 'ramda'
import { useDefaultLanguage, getLangaugeId } from 'commons/useDefaultLanguage'
import { mapVoucherToApi, createTranslationVoucherPayload } from './mapVoucher'
import { isEmptyOrNil } from 'libs/empty'
import { networkErrorToMessage } from 'commons/error'
import { useIntl } from 'react-intl'

import { errorTypes } from 'commons/error'

const initialState = {
  isLoading: false,
  values: undefined,
  errors: undefined,
}

const TYPE_LOADING = 'loading'
const TYPE_SUCCESS = 'success'
const TYPE_ERROR = 'error'
const TYPE_INIT = 'initial'

const submitReducer = (state, action) => {
  switch (action.type) {
    case TYPE_LOADING:
      return {
        ...state,
        isLoading: true,
        values: undefined,
        errors: undefined,
      }
    case TYPE_SUCCESS:
      return {
        ...state,
        isLoading: false,
        values: action.values,
        errors: undefined,
        isSuccess: true,
      }
    case TYPE_ERROR:
      return {
        ...state,
        isLoading: false,
        values: undefined,
        errors: action.errors,
      }
    case TYPE_INIT:
      return initialState
    default:
      return state
  }
}

export default (onCreateSuccess, editId) => {
  const dispatch = useDispatch()
  const language = useDefaultLanguage()
  const defaultLanguageId = getLangaugeId(language)
  const { formatMessage } = useIntl()
  const [submitState, submitDispatch] = useReducer(submitReducer, initialState)

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (submitState.isLoading === false && !R.isNil(submitState.isSuccess)) {
      submitDispatch({ type: TYPE_INIT })
      onCreateSuccess(submitState.values)
    }
  }, [submitState.values, submitState.isLoading, onCreateSuccess])
  /* eslint-enable react-hooks/exhaustive-deps */

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (submitState.errors) {
      dispatch(
        MessageAlert.showError(
          networkErrorToMessage(formatMessage)(
            submitState.errors,
            errorTypes.voucher,
          ),
        ),
      )
    }
  }, [dispatch, submitState.errors])
  /* eslint-enable react-hooks/exhaustive-deps */

  const submit = useCallback(
    (submitModel) => (translations) => {
      const apiEffect = async (vouchers) => {
        if (isEmptyOrNil(editId)) return effects.apis.createVoucher(vouchers)
        else
          return effects.apis.updateVoucher(
            editId,
            R.mergeRight(vouchers, { id: editId }),
          )
      }

      apiEffect(mapVoucherToApi(submitModel)(translations))
        .then((resp) => {
          submitDispatch({ type: TYPE_SUCCESS, values: resp })
        })
        .catch((errors) =>
          submitDispatch({
            type: TYPE_ERROR,
            errors,
          }),
        )
    },
    [editId],
  )

  const translateAndSubmit = useCallback(
    (submitModel) => {
      submitDispatch({ type: TYPE_LOADING })

      const getDefaultLanguageList = () => [
        createTranslationVoucherPayload(defaultLanguageId)(submitModel),
      ]

      const defaultLanguageList = getDefaultLanguageList()
      if (defaultLanguageList && defaultLanguageList[0]) {
        delete defaultLanguageList[0]['languageId']
      }

      submit(submitModel)(defaultLanguageList)
    },
    [defaultLanguageId, submit],
  )

  return {
    submitVoucher: translateAndSubmit,
    isSubmitting: submitState.isLoading,
  }
}
